import type { Distribution } from "@memorang/types";
/* ----------------- React --------------- */
import { useCallback, useState } from "react";

/* ----------------- Hooks --------------- */
import useSessionStore from "./useSessionStore";

/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";

/* ----------------- Constants --------------- */
import { events } from "@constants/tracking";

import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import useCacheKey from "@hooks/useCacheKey";
import type { BackendSessionResponse, SessionResponse } from "@memorang/types";
import type { ExamStatus } from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { createSession, endSession, fetchSessionItems } from "../api";
import type { CreateSessionRequest, ResumeSessionRequest } from "../api/types";
import { flattenSessionItems } from "../helpers/session";
import useAIFeedbackStore from "./useAIFeedbackStore";

export const useSession = () => {
	const router = useRouter();

	const { showToast } = useToast();

	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const resetAiFeedbackStore = useAIFeedbackStore(
		(state) => state.resetAiFeedbackStore,
	);
	const updateProgress = useSessionStore((state) => state.updateProgress);

	const resumeSessionMutation = useMutation({
		mutationFn: fetchSessionItems,
	});

	const {
		currentExamContext: { organizationId, projectId, studyPackCollectionId },
	} = useExamContext();
	const { baseCacheKey } = useCacheKey();

	const resumeSession = (request: ResumeSessionRequest) => {
		return resumeSessionMutation.mutateAsync(request);
	};
	const queryClient = useQueryClient();
	const updateEndingSessionInProgress = useSessionStore(
		(state) => state.updateEndingSessionInProgress,
	);

	const toggleShowErrorDialog = () => {
		setShowErrorDialog((prev) => !prev);
	};

	const createSessionMutation = useMutation({
		mutationFn: createSession,
	});

	const endSessionMutation = useMutation({
		mutationKey: [baseCacheKey, cacheKeys.endSession],
		mutationFn: endSession,
		onSuccess: () => {
			updateEndingSessionInProgress(false);
			queryClient.invalidateQueries();
		},
	});

	const createNewSession = useCallback(
		(request: CreateSessionRequest) => {
			return createSessionMutation.mutateAsync(request);
		},
		[createSessionMutation],
	);

	const endCurrentSession = useCallback(
		({ id }: { id: number }) => {
			updateEndingSessionInProgress(true);
			return endSessionMutation.mutateAsync({ sessionId: id });
		},
		[updateEndingSessionInProgress, endSessionMutation.mutateAsync],
	);

	const handlePostSessionCreation = ({
		newSessionResponse,
		numItemsRequested,
		dist,
		tags,
		handleClose,
		push,
		isDiagnostic,
		lastUpdatedItemIndex,
		isSummativeTest,
	}: {
		newSessionResponse: SessionResponse;
		numItemsRequested: number;
		dist: Distribution;
		tags?: number[][];
		handleClose?: () => void;
		push?: boolean;
		lastUpdatedItemIndex?: number;
		isDiagnostic?: boolean;
		isSummativeTest?: boolean;
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	}) => {
		trackCustomEvent({
			eventName:
				lastUpdatedItemIndex && lastUpdatedItemIndex > 0
					? events.sessionResumed
					: events.sessionStarted,
			numItems: numItemsRequested,
			dist: JSON.stringify(dist),
			tags: JSON.stringify(tags || []),
			type: isDiagnostic ? "DIAGNOSTIC_SESSION" : "FORMATIVE_SESSION",
		});
		if (lastUpdatedItemIndex && lastUpdatedItemIndex > 0) {
			const numItems = newSessionResponse.sessionItems.length;
			const progress = (lastUpdatedItemIndex + 1) / numItems;
			updateProgress(progress);
		}
		updateSession({
			sessionResponse: newSessionResponse,
			numItemsRequested,
			isSummativeTest,
			isDiagnostic,
		});
		if (handleClose || push) {
			handleClose?.();
			router.push({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
					index:
						lastUpdatedItemIndex && lastUpdatedItemIndex > 0
							? lastUpdatedItemIndex + 1
							: 1,
				},
			});
		} else {
			router.replace({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
					index:
						lastUpdatedItemIndex && lastUpdatedItemIndex > 0
							? lastUpdatedItemIndex + 1
							: 1,
				},
			});
		}
	};
	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const handleCreateCommonSession = useCallback(
		async ({
			dist,
			tags,
			handleClose,
			itemIds,
			push,
			additionalSessionParams,
		}: {
			dist: Distribution;
			tags?: number[][];
			handleClose?: () => void;
			itemIds?: number[];
			push?: boolean;
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
				examStatus?: ExamStatus;
				sessionId?: number;
				collectionId?: number;
			};
		}) => {
			resetSessionStore();
			resetAiFeedbackStore();
			try {
				const numItemsRequested = Object.values(dist).reduce(
					(acc, curr) => acc + curr,
					0,
				);

				let result: BackendSessionResponse;
				if (
					additionalSessionParams?.examStatus === "ACTIVE" &&
					additionalSessionParams?.sessionId
				) {
					result = await resumeSession({
						sessionId: additionalSessionParams?.sessionId,
					});
				} else {
					const params = {
						collectionId:
							additionalSessionParams?.collectionId || studyPackCollectionId!,
						projectId,
						organizationId,
						sessionType: additionalSessionParams ? "SUMMATIVE" : "FORMATIVE",
						sessionSubType: additionalSessionParams?.sessionSubType,
						tagIds: tags,
						itemIds,
						itemsSelected: numItemsRequested,
						itemsSelectedDistribution: dist,
						title: additionalSessionParams?.title,
						icon: additionalSessionParams?.icon,
						examId: additionalSessionParams?.examId,
					} as const;

					result = await createNewSession(params);
				}

				if (!result.sessionItems?.length) {
					showToast({
						message: "Failed to create session, try again",
					});
					const error = new Error(
						"Error creating formative session : session list is empty",
					);
					captureErrorToSentry(error, {
						error: serializeErrorForSentry(result),
					});
					return;
				}

				const flattenedSessionItems = flattenSessionItems(result.sessionItems);

				// const newSessionResponse = finalMockedSessionResponse;

				const newSessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				handlePostSessionCreation({
					newSessionResponse,
					numItemsRequested,
					dist,
					tags,
					handleClose,
					push,
					isDiagnostic:
						additionalSessionParams?.sessionSubType === "DIAGNOSTIC",
					isSummativeTest: !!additionalSessionParams?.collectionId,
				});
			} catch (_error) {
				toggleShowErrorDialog();
				const error = new Error("Error creating formative session");
				captureErrorToSentry(error, {
					error: serializeErrorForSentry(_error),
				});
			}
		},
		[resetSessionStore, router, updateSession, createNewSession],
	);

	return {
		handleCreateCommonSession,
		showErrorDialog,
		toggleShowErrorDialog,
		createNewSession,
		endCurrentSession,
		resumeSession,
		isCreatingSession:
			createSessionMutation.isPending || resumeSessionMutation.isPending,
		isEndingSession:
			endSessionMutation.isPending || resumeSessionMutation.isPending,
		createSessionError:
			createSessionMutation.error || resumeSessionMutation.error,
		endSessionError: endSessionMutation.error,
	};
};
